import clsx from 'clsx';
import React from 'react';

import { Logger } from '@gitbook/util-debug';
import { useSafeContext } from '@gitbook/util-react-hooks';

const logger = Logger('Layout');

export type LayoutKind = 'centered' | 'full-width' | 'full-screen-split' | 'full-screen-stack';
export type LayoutProps = {
    layout?: LayoutKind;
};

export type LayoutContextType = LayoutProps;

const LayoutContext = React.createContext<LayoutContextType | null>(null);

export function useLayout() {
    return useSafeContext(LayoutContext, 'useLayout must be used within a LayoutContextProvider');
}

export function LayoutContextProvider(props: { children: React.ReactNode } & LayoutProps) {
    const { children, layout = 'full-width' } = props;

    const value = React.useMemo(
        () => ({
            layout,
        }),
        [layout]
    );

    return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

/**
 * Custom Layout utility component. For setting centered or full width layouts
 * for smaller portions of the page.
 *
 * @example ./Page.stories.tsx#PageHomeTest
 */
export function Layout(props: {
    children: React.ReactNode;
    className?: string;
    slotClassName?: string;
    kind?: LayoutKind;
    /**
     *  Wide layout with a max-width of 77.5rem
     **/
    wide?: boolean;
}) {
    const { layout } = useLayout();
    const { children, className, kind = layout, slotClassName, wide } = props;
    switch (kind) {
        case 'centered':
            return (
                <div
                    className={clsx(
                        'grid w-full grid-cols-[minmax(min(var(--padding),100%),1fr),minmax(min(calc(var(--padding)_*_2),100%),var(--max-width)),minmax(min(var(--padding),100%),1fr)] [--max-width:61.25rem] [--padding:1.5rem] lg:[--padding:4rem]',
                        wide && '[--max-width:77.5rem]',
                        className
                    )}
                >
                    <div className={clsx('col-[2]', slotClassName)}>{children}</div>
                </div>
            );
        case 'full-screen-split':
            return (
                <div
                    className={clsx(
                        'grid h-screen max-h-screen w-full auto-cols-fr grid-flow-col grid-rows-1',
                        className
                    )}
                >
                    {children}
                </div>
            );
        case 'full-screen-stack':
            if (process.env.NODE_ENV === 'development' && React.Children.count(children) !== 3) {
                logger.warning(
                    'Stack works best with a header, main content area and footer, the main content area will expand to push the footer to the bottom of the page.'
                );
            }
            return (
                <div className={clsx('grid h-screen w-full grid-rows-[auto,1fr,auto]', className)}>
                    {children}
                </div>
            );
        default:
            return <div className={clsx('w-full', className)}>{children}</div>;
    }
}

/**
 * `LayoutRoot` provides a context for sharing a default layout to `Layout` components.
 */
export const LayoutRoot = React.forwardRef<
    HTMLDivElement,
    { className?: string; children: React.ReactNode } & LayoutProps
>(function LayoutRoot(props, ref) {
    const { children, className, layout, ...rest } = props;
    return (
        <LayoutContextProvider layout={layout}>
            <div ref={ref} className={clsx('w-full overflow-y-auto', className)} {...rest}>
                {children}
            </div>
        </LayoutContextProvider>
    );
});
